import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../shadcn/ui/Dialog";
import { Button } from "../../../shadcn/ui/Button";
import { Label } from "../../../shadcn/ui/Label";
import { Textarea } from "../../../shadcn/ui/Textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shadcn/ui/Select";
import { rfqService } from "@b2b/services";
import { RfqDispute } from "@b2b/services/api/modules/rfq/rfq.types";

interface RFQDisputeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rfqId: string;
  quoteId?: string;
  onDisputeCreated?: () => void;
  existingDisputes?: RfqDispute[];
  mode?: "create" | "view";
}

const DISPUTE_REASONS = [
  "PRICE_MISMATCH",
  "QUALITY_ISSUE",
  "DELIVERY_DELAY",
  "TERMS_DISAGREEMENT",
  "OTHER",
];

const REASON_LABELS: Record<string, string> = {
  PRICE_MISMATCH: "Price Mismatch",
  QUALITY_ISSUE: "Quality Issue",
  DELIVERY_DELAY: "Delivery Delay",
  TERMS_DISAGREEMENT: "Terms Disagreement",
  OTHER: "Other",
};

export const RFQDisputeDialog = ({
  open,
  onOpenChange,
  rfqId,
  quoteId,
  onDisputeCreated,
  existingDisputes = [],
  mode = "create",
}: RFQDisputeDialogProps) => {
  const [reason, setReason] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [disputes, setDisputes] = useState<RfqDispute[]>(existingDisputes);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && mode === "view") {
      fetchDisputes();
    }
  }, [open, mode, rfqId]);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const response = await rfqService.getDisputes(rfqId);
      setDisputes(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching disputes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!reason) {
      return;
    }

    try {
      setSubmitting(true);
      await rfqService.createDispute(rfqId, {
        quoteId,
        reason,
        description: description || "",
      });
      onDisputeCreated?.();
      setReason("");
      setDescription("");
      onOpenChange(false);
      if (mode === "view") {
        fetchDisputes();
      }
    } catch (error) {
      console.error("Error creating dispute:", error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "DISMISSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Raise Dispute" : "Disputes"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Report an issue with this RFQ or quote"
              : "View all disputes for this RFQ"}
          </DialogDescription>
        </DialogHeader>

        {mode === "create" ? (
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="reason">Reason *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id="reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {DISPUTE_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {REASON_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide additional details about the dispute..."
                rows={4}
              />
            </div>
          </div>
        ) : (
          <div className="py-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading disputes...
              </div>
            ) : disputes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No disputes found
              </div>
            ) : (
              <div className="space-y-4">
                {disputes.map((dispute) => (
                  <div
                    key={dispute.id}
                    className="border rounded-lg p-4 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {REASON_LABELS[dispute.reason] || dispute.reason}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs ${getStatusColor(
                            dispute.status
                          )}`}
                        >
                          {dispute.status}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(dispute.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {dispute.description && (
                      <p className="text-sm text-muted-foreground">
                        {dispute.description}
                      </p>
                    )}
                    {dispute.resolvedAt && (
                      <p className="text-xs text-muted-foreground">
                        Resolved:{" "}
                        {new Date(dispute.resolvedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode === "create" && (
            <Button
              onClick={handleSubmit}
              disabled={!reason || submitting}
              variant="primary"
            >
              {submitting ? "Submitting..." : "Submit Dispute"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RFQDisputeDialog;
